import {
  CatalogFiltersState,
  CatalogFiltersData,
  AdditionalFilters,
  Tag
} from '../../../types'

/**
 * Класс для управления состоянием фильтров каталога
 */
export class FiltersState {
  private state: CatalogFiltersState
  private tags: Tag[]

  /**
   * Создаёт экземпляр FiltersState
   *
   * @param {Tag[]} tags - Массив тегов
   */
  constructor(tags: Tag[]) {
    this.tags = tags
    this.state = {
      type: '',
      categories: [],
      complexity: [],
      horror: [],
      metro: [],
      age: '',
      withPlayers: false,
      original: false,
      additionalFilters: {},
      showFilters: false,
      haveNonDefaultAdditionalFilters: false
    }
  }

  /**
   * Возвращает копию текущего состояния фильтров
   *
   * @return {CatalogFiltersState} Копия состояния
   */
  public getState(): CatalogFiltersState {
    return { ...this.state }
  }

  /**
   * Устанавливает тип квеста
   *
   * @param {string} value - Значение типа
   */
  public setType(value: string): void {
    this.state.type = value
  }

  /**
   * Возвращает текущий тип квеста
   *
   * @return {string} Тип квеста
   */
  public getType(): string {
    return this.state.type
  }

  /**
   * Устанавливает выбранные категории
   *
   * @param {string[]} value - Массив идентификаторов категорий
   */
  public setCategories(value: string[]): void {
    this.state.categories = value
  }

  /**
   * Возвращает выбранные категории
   *
   * @return {string[]} Массив категорий
   */
  public getCategories(): string[] {
    return this.state.categories
  }

  /**
   * Очищает выбранные категории
   */
  public clearCategories(): void {
    this.state.categories = []
  }

  /**
   * Устанавливает уровни сложности
   *
   * @param {string[]} value - Массив уровней
   */
  public setComplexity(value: string[]): void {
    this.state.complexity = value
  }

  /**
   * Возвращает уровни сложности
   *
   * @return {any} Массив уровней
   */
  public getComplexity(): string[] {
    return this.state.complexity
  }

  /**
   * Устанавливает уровни страха
   *
   * @param {number[]} value - Массив уровней
   */
  public setHorror(value: string[]): void {
    this.state.horror = value
  }

  /**
   * Возвращает уровни страха
   *
   * @return {any} Массив уровней
   */
  public getHorror(): string[] {
    return this.state.horror
  }

  /**
   * Устанавливает выбранные станции метро
   *
   * @param {string[]} value - Массив станций метро
   */
  public setMetro(value: string[]): void {
    this.state.metro = value
  }

  /**
   * Возвращает выбранные станции метро
   *
   * @return {string[]} Массив станций метро
   */
  public getMetro(): string[] {
    return this.state.metro
  }

  /**
   * Устанавливает максимальный возраст игрока
   *
   * @param {string} value - Значение возраста
   */
  public setAge(value: string): void {
    this.state.age = value
  }

  /**
   * Возвращает выбранный возраст игрока
   *
   * @return {string} Возраст игрока
   */
  public getAge(): string {
    return this.state.age
  }

  public setWithPlayers(value: number | boolean): void {
    this.state.withPlayers = value
  }

  public getWithPlayers(): number | boolean {
    return typeof this.state.withPlayers === 'number'
      ? this.state.withPlayers > 0
      : this.state.withPlayers
  }

  public toggleWithPlayers(): void {
    if (this.state.withPlayers) {
      this.state.withPlayers = false
    } else {
      this.state.withPlayers = true
    }
  }

  /**
   * Устанавливает дополнительные фильтры
   *
   * @param {AdditionalFilters} filters - Объект дополнительных фильтров
   * @param {boolean} hasNonDefault - Есть ли непустые фильтры
   */
  public setAdditionalFilters(
    filters: AdditionalFilters,
    hasNonDefault: boolean
  ): void {
    this.state.additionalFilters = filters
    this.state.haveNonDefaultAdditionalFilters = hasNonDefault
  }

  /**
   * Возвращает дополнительные фильтры
   *
   * @return {any} Объект дополнительных фильтров
   */
  public getAdditionalFilters(): AdditionalFilters {
    return this.state.additionalFilters
  }

  /**
   * Проверяет наличие непустых дополнительных фильтров
   *
   * @return {any} true, если есть непустые фильтры
   */
  public hasNonDefaultAdditionalFilters(): boolean {
    return this.state.haveNonDefaultAdditionalFilters
  }

  /**
   * Устанавливает флаг отображения фильтров
   *
   * @param {boolean} value - Показывать ли фильтры
   */
  public setShowFilters(value: boolean): void {
    this.state.showFilters = value
  }

  /**
   * Возвращает флаг отображения фильтров
   *
   * @return {any} true, если фильтры должны отображаться
   */
  public getShowFilters(): boolean {
    return this.state.showFilters
  }

  /**
   * Включает или выключает фильтр «Только Клаустрофобия»
   *
   * @param {boolean} value - Показывать только квесты оригинала
   */
  public setOriginal(value: boolean): void {
    this.state.original = value
  }

  /**
   * Возвращает состояние фильтра «Только Клаустрофобия»
   *
   * @return {boolean} true, если включён
   */
  public getOriginal(): boolean {
    return this.state.original
  }

  /**
   * Переключает фильтр «Только Клаустрофобия»
   */
  public toggleOriginal(): void {
    this.state.original = !this.state.original
  }

  /**
   * Проверяет, есть ли активные фильтры
   *
   * @return {any} true, если есть активные фильтры
   */
  public isActiveFilter(): boolean {
    const isWithPlayers =
      typeof this.state.withPlayers === 'number'
        ? this.state.withPlayers > 0
        : this.state.withPlayers

    return (
      this.state.type !== '' ||
      this.state.categories.length > 0 ||
      this.state.horror.length > 0 ||
      this.state.complexity.length > 0 ||
      this.state.metro.length > 0 ||
      this.state.age !== '' ||
      isWithPlayers ||
      this.state.original ||
      this.state.haveNonDefaultAdditionalFilters
    )
  }

  /**
   * Сбрасывает все фильтры к значениям по умолчанию
   */
  public reset(): void {
    this.state.type = ''
    this.state.categories = []
    this.state.complexity = []
    this.state.horror = []
    this.state.metro = []
    this.state.age = ''
    this.state.withPlayers = false
    this.state.original = false
    this.state.additionalFilters = {}
    this.state.haveNonDefaultAdditionalFilters = false
  }

  /**
   * Возвращает данные фильтров в формате для передачи
   *
   * @return {any} Объект с данными фильтров
   */
  public getFiltersData(): CatalogFiltersData {
    return {
      type: this.state.type,
      categories: this.state.categories.map((tagSlug) => {
        const tag = this.tags.find((t) => t.slug === tagSlug)
        return tag?.name || tagSlug
      }),
      complexity: this.state.complexity,
      horror: this.state.horror,
      metro: this.state.metro,
      age: this.state.age,
      playersCount: this.state.withPlayers,
      original: this.state.original,
      ...this.state.additionalFilters
    }
  }
}
